import { Directive, OnDestroy, OnInit, inject } from '@angular/core';
import { AbstractCheckoutPayment } from '@df/checkout/abstract-checkout-payment.component.def';
import { Checkout } from '@df/checkout/checkout.interfaces';
import { Braintree } from '@df/module-braintree/braintree.namespace';
import { BraintreeService } from '@df/module-braintree/braintree.service';
import { NgInject } from '@df/ng/ng-inject';
import { IBasketTotals } from '@df/session/api/basket/basket';

@Directive()
// tslint:disable-next-line: directive-class-suffix
export abstract class AbstractBraintreeComponent<T extends Braintree.IPaymentMethod>
    extends AbstractCheckoutPayment<T>
    implements OnInit, OnDestroy
{
    /**
     * Component Id required for selectors
     * @type {number}
     */
    private _componentId?: number;

    /**
     * Payment method code related to component
     * @type {string}
     */
    protected abstract override readonly methodCode: Braintree.Methods | Braintree.LocalPaymentMethods;

    protected braintreeService = inject(BraintreeService);

    override ngOnInit() {
        super.ngOnInit();
        this.subscriptions.add(
            this.events.on(Checkout.EVENTS.SubmitError).subscribe((data: Checkout.ISubmitErrorEventData) => {
                if (this.isSelectedInRequest(data.request!)) {
                    this.onCheckoutError(data);
                }
            })
        );
        if (this.checkoutStepComponent) {
            this.subscriptions.add(
                this.checkoutStepComponent.submittedSubject.subscribe((submitted: boolean) => {
                    if (submitted) {
                        this.onStepSubmitted();
                    }
                })
            );
        }
    }

    /**
     * component id getter
     * @returns {number}
     */
    get componentId(): number {
        if (!this._componentId) {
            this._componentId = this.braintreeService.getComponentId();
        }
        return this._componentId;
    }

    /**
     * Returns gran total from basket
     * @returns {number}
     */
    protected getTotal(): number | undefined {
        return this.getTotals()?.grand_total;
    }

    /**
     * Returns basket totals
     * @returns {IBasketTotals}
     */
    protected getTotals(): IBasketTotals {
        return this.app.customer.basket.totals!;
    }

    //////////////////////////////////////////
    /////
    /////   Payment method configuration
    /////
    //////////////////////////////////////////

    /**
     * If payment method should be initiated before selecting
     * @returns {boolean}
     */
    protected override preload(): boolean {
        return true;
    }

    /**
     * Returns client token for authorization
     * @returns {string}
     */
    protected getClientToken(): string {
        return this.method!.config.clientToken;
    }

    //////////////////////////////////////////
    /////
    /////   Checkout
    /////
    //////////////////////////////////////////

    /**
     * On checkout submit error callback
     * @param {Checkout.ISubmitErrorEventData} data
     */
    protected abstract onCheckoutError(data: Checkout.ISubmitErrorEventData): void;

    /**
     * Returns if this method was used in submission request
     * @returns {boolean}
     */
    protected isSelectedInRequest(submissionData: Checkout.ISubmissionData): boolean {
        return submissionData.paymentMethod.method === this.methodCode;
    }

    /**
     * On checkout step submission callback
     */
    protected onStepSubmitted(): void {}
}
